import React from 'react';
import DynamicScreen from './';
import ScreenSequence from '../../types/sequence';
import { useHistory } from 'react-router-dom';
import dlv from 'dlv';

const Context = React.createContext({});

function useContext() {
    return React.useContext(Context);
}

function Provider({children, ...props}) {
	const dProps = {
		hasParent:true
	};
	return <Context.Provider children={children} value={{...dProps, ...props}} />
}

function identifyChildScreens(descriptor, acc, parentIndex) {
	if(Array.isArray(descriptor)) {
		descriptor.forEach((d) => {
			if(d.id){
				acc.push({id:d.id, index:parentIndex, isChildScreen:true});
			} else {
				identifyChildScreens(d, acc, parentIndex);
			}
		})
	}
}

/**
 * This function builds a list of screen ids for a ScreenSequence based on the 
 * DynamicScreen descriptors, and their id value.
 */
function identifyScreens (screens) {
	return screens.reduce((acc, screen, i) => {
		const descriptor = dlv(screen, "props.descriptor") || (screen.id && screen);
		if(descriptor) {
			const screenId = dlv(descriptor, "id");
			if(screenId) acc.push({id:screenId, index:i, isChildScreen:false});
			identifyChildScreens(descriptor, acc, i);
		}
		return acc;
	}, []);
}

const DynamicScreenSequence = React.memo(
	({descriptor, template, onTemplate, activeTemplate, ...props}) => (
		<ScreenSequence {...props} identifyScreens={identifyScreens}>
			{ descriptor.map((screen, i) => 
				<DynamicScreen 
					descriptor={screen} 
					template={template} 
					key={`screen-${i}`} 
					onTemplate={onTemplate} 
					activeTemplate={activeTemplate}
					/>
			)}
		</ScreenSequence>
	)
);

/**
 * The DynamicScreenSequenceRoot will always be the top-most component in 
 * a Sequence that uses the default export form this page.
 * 
 * It offers callbacks for handling screen ids as they are hit, 
 * as well as sending the sequence to a specific id.
 * 
 * This component will map all ids hit to the location hash.
 * It will also cause browser history navigation to update 
 * the sequence.
 */
const DynamicScreenSequenceRoot  = React.memo(props => {

	const h = useHistory();
	const [screenState, setScreenState] = React.useState({
		fromSequence:null,
		fromHash:null
	});

	const onScreenDisplay = screen => {
		if(screen && screen.id && screenState.fromHash === screen.id) {
			setScreenState({fromSequence:screen.id, fromHash:null});
		} else if(screen && screen.id && !screenState.fromHash && screenState.fromSequence !== screen.id) {
			setScreenState({fromSequence:screen.id, fromHash:null});
		}
	}
	
	React.useEffect(() => {
		if(!h || !h.location) return;
		const existinghash = h.location.hash && h.location.hash.substr(1);
		if(screenState.fromSequence && screenState.fromSequence !== existinghash) {
			h.push(`#${screenState.fromSequence}`);
		}


		const l = h.listen((location) => {
			const desiredScreenId = location.hash && location.hash.substr(1);
			if(desiredScreenId && desiredScreenId !== screenState.fromSequence) {
				setScreenState({fromSequence:null, fromHash:desiredScreenId});
			}	
		});
		return () => l();
	}, [h, screenState]);

	const screenToDraw = (screenState && (screenState.fromSequence || screenState.fromHash)) || null;
	return <Provider><DynamicScreenSequence {...props} goToScreenId={screenToDraw} onScreenDisplay={onScreenDisplay}/></Provider>;
});

/**
 * Using this component as a DynamicScreenSequence ensures that the top-most node 
 * is a DynamicScreenSequenceRoot.
 */
const DynamicScreenSequenceChooser = React.memo(props => {
	const { hasParent } = useContext();
	if( hasParent ) {
		return <DynamicScreenSequence {...props}/>;
	} else {
		return <DynamicScreenSequenceRoot {...props}/>
	}	
});

export default DynamicScreenSequenceChooser;